import { Button } from "@controls";
import { FrustratedPenguinAndErrorDocument } from "@illustrations";
import Modal from "@src/components/modal";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import { useOrganizations } from "@src/modules/member/hooks/organizations";
import {
  useHasPermission,
  usePermissions,
} from "@src/modules/member/hooks/use-permissions";
import { DASHBOARD_PATH } from "@src/modules/member/modules/main/router/paths";
import { NEW_ORGANIZATION_PATH } from "@src/modules/member/modules/settings/router/paths";
import { buildMemberPath, toMemberPath } from "@src/modules/member/path";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function PortalAccessOverlay({
  children,
}: {
  children: React.ReactNode;
}) {
  const { role } = usePermissions();
  const hasPortalAccess = useHasPermission(PERMISSIONS.MEMBER_PORTAL_ACCESS);

  if (!role) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-page">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-divider border-t-primary" />
      </div>
    );
  }

  if (hasPortalAccess) {
    return <>{children}</>;
  }

  return <PortalAccessDenied />;
}

function PortalAccessDenied() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [switchModalOpen, setSwitchModalOpen] = useState(false);

  const { currentOrganizationCode } = useOrganizationContext();
  const { data: organizationsResponse } = useOrganizations();
  const organizations = organizationsResponse?.data ?? [];

  const switchableOrganizations = useMemo(
    () =>
      organizations.filter(
        (org) =>
          org.code !== currentOrganizationCode &&
          org.role?.permissions?.includes(PERMISSIONS.MEMBER_PORTAL_ACCESS),
      ),
    [organizations, currentOrganizationCode],
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-page">
        <div className="flex flex-col items-center gap-6 max-w-md px-4 text-center">
          <div className="w-full max-w-[300px]">
            <FrustratedPenguinAndErrorDocument />
          </div>
          <h2 className="text-xl font-semibold text-default">
            {t("portalAccessDeniedTitle")}
          </h2>
          <p className="text-muted">{t("portalAccessDeniedDescription")}</p>
          <div className="flex flex-col gap-3 w-full">
            {switchableOrganizations.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setSwitchModalOpen(true)}
              >
                {t("switchOrganization")}
              </Button>
            )}
            <Button
              type="button"
              onClick={() => navigate(toMemberPath(NEW_ORGANIZATION_PATH()))}
            >
              {t("createOrganization")}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={switchModalOpen}
        onOpenChange={setSwitchModalOpen}
        title={t("switchOrganization")}
        description={t("switchOrganizationMessage")}
      >
        <div className="my-4">
          {switchableOrganizations.map((organization) => (
            <button
              key={organization.code}
              aria-label={t("switchOrganizationTo", {
                organization: organization.name,
              })}
              role="menuitem"
              onClick={() => {
                navigate(
                  `${buildMemberPath(organization.code)}/${DASHBOARD_PATH()}`,
                );
                setSwitchModalOpen(false);
              }}
              className="w-full py-2 px-4 text-left bg-tint my-1 rounded focus:outline-none focus:ring-1 focus:ring-focus hover:bg-tint"
            >
              {organization.name}
            </button>
          ))}
        </div>
        <div className="flex gap-2 justify-end">
          <div className="min-w-24">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSwitchModalOpen(false)}
            >
              {t("close")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
